/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/
/**
 * An iModel.js App can be embedded in an iFrame by passing the query param `embedded-url=<url of host>`.
 *
 * In addition to the "embedded-url" param, the following query params are supported and used in the following client library;
 * - "projectId" or "projectNumber"
 *   - Warning: A Connect Project Number is not guaranteed to stay consistent throughout the lifetime of a project and does not have to be unique across organizations.
 * - "imodelId"
 * - "ChangeSetId"
 * - "imodelindex"
 *   - If set to false, it will skip showing the iModel Selector page in the beginning.
 *
 * When the iModel.js application is embedded, sign-in cannot take place within the actual iFrame so instead a popup window
 * is opened to handle the sign-in.
 */
import { IFrameCommands, IFrameParentEvents } from "./Events.js";
const domContentLoaded = new Promise((resolve) => document.addEventListener("DOMContentLoaded", resolve));
/** The iModel.js Apps supported by default. */
export var EmbeddedAppOrigins;
(function (EmbeddedAppOrigins) {
    EmbeddedAppOrigins["DesignReview"] = "https://review.itwin.bentley.com";
    EmbeddedAppOrigins["PlantSight"] = "https://plantsightvisualizer.bentley.com";
})(EmbeddedAppOrigins || (EmbeddedAppOrigins = {}));
// Set by the initialize method
let hostOrigin;
let contentWindow;
/** Uses the [Window.postMessage](https://developer.mozilla.org/docs/Web/API/Window/postMessage) method to send messages to
 * the window contenting the embedded app.
 */
function postAppMessage(type, payload) {
    if (undefined === hostOrigin)
        throw new Error("The origin to send messages is undefined.  Please run EmbedAPI.initialize().");
    if (contentWindow)
        contentWindow.postMessage({ type, payload }, hostOrigin);
}
/** Selects the Elements provided.
 *
 * The valid SelectionScopes are;
 *  - "element"
 *  - "functional-assembly"
 *  - "functional-element"
 *  - "functional-top-assembly"
 *  - etc.
 */
async function selectElements(ids, selectionScope) {
    const payload = {
        ids,
        selectionScope,
    };
    postAppMessage(IFrameCommands.SelectElements, payload);
}
/**  Sets the Elements with ids provided to display with a color override.
 * @param ids The IDs of the elements
 * @param color An integer representation of a color to override.  The color should be in the format 0xBBGGRR.
 */
async function overrideElements(ids, color) {
    const payload = {
        ids,
        color,
    };
    postAppMessage(IFrameCommands.OverrideElements, payload);
}
/** Show the dimensions of the range provided in the view
 * @param payload ShowSectionRangePayload object containing the range3d object displayed in the view
 */
function showRange(payload) {
    postAppMessage(IFrameCommands.ShowSectionRange, payload);
}
/** Clears the range displayed in the view
 */
function clearRange() {
    postAppMessage(IFrameCommands.ClearSectionRange);
}
/** Clears the range displayed in the view
 */
function forceRefresh() {
    postAppMessage(IFrameCommands.ForceRefresh);
}
function loadExtension(payload) {
    postAppMessage(IFrameCommands.LoadExtension, payload);
}
/** Sends a custom message to the frame, related custom message listener must be registered on the application */
function customMessage(type, payload) {
    postAppMessage(IFrameCommands.CustomMessage, { customType: type, customPayload: payload });
}
/** Add a listener to handle custom event */
function addCustomEventListener(type, listener) {
    if (!customEventListeners[type]) {
        customEventListeners[type] = new Set();
    }
    customEventListeners[type].add(listener);
}
/** Remove a custom event listener */
function removeCustomEventListener(type, listener) {
    if (!customEventListeners[type]) {
        return;
    }
    customEventListeners[type].delete(listener);
}
const iModelQueryHandlers = {};
const iModelQueryIndicies = [];
/**  Returns a list of rows that are the result of query parameters provided
 * @param ecsql The ECSQL statement to execute
 * @param bindings The values to bind to the parameters (if the ECSQL has any).
 * Pass an *array* of values if the parameters are *positional*.
 * Pass an *object of the values keyed on the parameter name* for *named parameters*.
 * The values in either the array or object must match the respective types of the parameters.
 * See "[iModel.js Types used in ECSQL Parameter Bindings]($docs/learning/ECSQLParameterTypes)" for details.
 * @param limitRows Specify upper limit for rows that can be returned by the query.
 * @param quota Specify non binding quota. These values are constrained by global setting
 * but never the less can be specified to narrow down the quota constraint for the query but staying under global settings.
 * @param priority Specify non binding priority for the query. It can help user to adjust
 * priority of query in queue so that small and quicker queries can be prioritized over others.
 * @throws If there was any error while submitting, preparing or stepping into query.
 */
async function queryIModel(ecsql, bindings, limitRows) {
    var _a;
    let id;
    if (iModelQueryIndicies.length === 0) {
        id = Object.keys(iModelQueryHandlers).length;
    }
    else {
        id = iModelQueryIndicies.shift();
    }
    const responsePromise = new Promise((resolve) => (iModelQueryHandlers[id] = resolve));
    const payload = {
        ecsql,
        bindings,
        limitRows,
        id,
    };
    postAppMessage(IFrameCommands.IModelQuery, payload);
    const response = await responsePromise;
    // Handle new error response as part of the response body.
    if (response && typeof response === "object" && ((_a = response) === null || _a === void 0 ? void 0 : _a.error)) {
        throw new Error(response.error);
    }
    return response && Array.isArray(response) ? response : [];
}
let elementPropsHandler;
async function getElementProps(ids) {
    const response = new Promise((resolve) => (elementPropsHandler = resolve));
    postAppMessage(IFrameCommands.GetElementInfo, ids);
    const props = await response;
    elementPropsHandler = undefined;
    return props;
}
function id64ArgToArray(ids) {
    if (typeof ids === "string")
        return [ids];
    return Array.isArray(ids) ? ids : Array.from(ids);
}
const viewOpenedListeners = new Set();
const selectionListeners = new Set();
const sectionRangeListeners = new Set();
const userChangedListeners = new Set();
const customEventListeners = {};
async function eventHandler(event) {
    var _a;
    // Do not accept any MessageEvents from an unknown origin.
    if (event.origin !== hostOrigin)
        return;
    if (event.data.type === IFrameParentEvents.IModelQueryResult) {
        const id = undefined === event.data.id ? 0 : event.data.id;
        if (undefined === iModelQueryHandlers[id])
            throw new Error("Something went wrong");
        iModelQueryHandlers[id](event.data.payload);
        delete iModelQueryHandlers[id];
        iModelQueryIndicies.push(id);
    }
    if (event.data.type === IFrameParentEvents.ElementProps) {
        if (elementPropsHandler)
            elementPropsHandler(event.data.payload);
    }
    if (event.data.type === IFrameParentEvents.SelectionChanged) {
        for (const listener of selectionListeners)
            listener(id64ArgToArray(event.data.payload));
    }
    if (event.data.type === IFrameParentEvents.SectionToolClipChanged) {
        for (const listener of sectionRangeListeners)
            listener(event.data.payload);
    }
    if (event.data.type === IFrameParentEvents.ViewOpened) {
        for (const listener of viewOpenedListeners)
            listener();
    }
    if (event.data.type === IFrameParentEvents.UserStateChanged) {
        for (const listener of userChangedListeners)
            listener();
    }
    if (event.data.type === IFrameParentEvents.CustomEvent) {
        const customType = (_a = event.data.payload) === null || _a === void 0 ? void 0 : _a.customType;
        if (customType && customEventListeners[customType]) {
            for (const listener of customEventListeners[customType])
                listener(event.data.payload.customPayload);
        }
    }
}
/** Initializes an iFrame, in the provided selector, containing an iModel.js App and optionally opens directly to an iModel.
 *
 * By default, the initialize method embeds Design Review and does not show the iModel Index page and opens to the default view of the iModel provided.
 *
 * Note: If both a ProjectId and ProjectNumber are provided, the Id will take precedence.
 *
 * @param selector  The Html selector to place the created iFrame.
 * @param opts  The options for initializing Design Review.
 */
export async function initialize(selector, opts) {
    if ("loading" === document.readyState)
        await domContentLoaded;
    const container = document.querySelector(selector);
    if (null === container)
        throw new Error("Could not find container element in DOM!");
    const iframe = document.createElement("iframe");
    const project = undefined !== opts.projectId
        ? `projectId=${opts.projectId}`
        : `projectNumber=${opts.projectNumber}`;
    const imodel = undefined !== opts.iModelId
        ? `iModelId=${opts.iModelId}`
        : `iModelName=${opts.iModelName}`;
    const issue = undefined !== opts.issueId
        ? `&issueId=${opts.issueId}`
        : "";
    const hideHeader = undefined !== opts.hideHeader
        ? opts.hideHeader
            ? "&embedded-servicebar-hidden=true"
            : "&embedded-servicebar-hidden=false"
        : "";
    const syncTheme = undefined !== opts.syncExternalTheme
        ? opts.syncExternalTheme
            ? "&syncExternalTheme=true"
            : "&syncExternalTheme=false"
        : "";
    const shareViewId = undefined !== opts.shareViewId
        ? `&shareViewId=${opts.shareViewId}`
        : "";
    const additionalQueryParameter = undefined !== opts.additionalQueryParameter
        ? `&${opts.additionalQueryParameter}`
        : "";
    hostOrigin = opts.appOrigin;
    if (undefined === hostOrigin)
        hostOrigin = EmbeddedAppOrigins.DesignReview;
    if (hostOrigin === EmbeddedAppOrigins.PlantSight)
        iframe.src = `${hostOrigin}/?${project}&imodelId=${opts.iModelId}&changesetId=${opts.changesetId}&show-imodel-index-stage=false&embeddedUrl=${location.origin}${additionalQueryParameter}`;
    else if (hostOrigin === EmbeddedAppOrigins.DesignReview)
        iframe.src = `${hostOrigin}/context/${opts.projectId}/imodel/${opts.iModelId}/?it3Mode&hideHeader&ChangeSetId=${opts.changesetId}${shareViewId}${issue}${hideHeader}${syncTheme}&imodelindex=false&embedded-url=${location.origin}${additionalQueryParameter}`;
    else // any other iModel.js apps
        iframe.src = `${hostOrigin}/imodeljs/?${project}&${imodel}&ChangeSetId=${opts.changesetId}${shareViewId}${issue}${hideHeader}${syncTheme}&imodelindex=false&embedded-url=${location.origin}${additionalQueryParameter}`;
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.borderStyle = "none";
    iframe.style.display = "block";
    container.appendChild(iframe);
    if (opts.customEventListeners) {
        for (const customType in opts.customEventListeners) {
            if (opts.customEventListeners.hasOwnProperty(customType)) {
                addCustomEventListener(customType, opts.customEventListeners[customType]);
            }
        }
    }
    // If the frame must be used for different iModels over the course of an application,
    // the state must be cleaned between each uses.
    const removeEmbeddedApp = () => {
        container.removeChild(iframe);
        window.removeEventListener("message", eventHandler);
        viewOpenedListeners.clear();
        selectionListeners.clear();
        sectionRangeListeners.clear();
        userChangedListeners.clear();
        for (const customType in customEventListeners)
            customEventListeners[customType].clear();
    };
    if (!iframe.contentWindow) {
        throw new Error("Created iframe has no content window!");
    }
    const api = await initializeContext(iframe.contentWindow, hostOrigin);
    return Object.assign(Object.assign({}, api), { removeEmbeddedApp });
}
/**
 * Initialize API with existing iModel.js App Context.
 *
 * @param appWindow   The window context of the iModel.js App
 * @param appOrigin   The origin url of the iModel.js App
 */
export async function initializeContext(appWindow, appOrigin) {
    window.addEventListener("message", eventHandler, false);
    contentWindow = appWindow;
    hostOrigin = appOrigin;
    return {
        showRange,
        clearRange,
        customMessage,
        loadExtension,
        forceRefresh,
        getElementProps,
        selectElements,
        emphasizeElements: (ids) => postAppMessage(IFrameCommands.EmphasizeElements, ids),
        overrideElements,
        /** Clear color/transparency overrides from elements. Removes all overrides when ids isn't supplied.
         * @param ids specify the IDs of elements to remove any color/transparency override from.
         */
        clearOverriddenElements: (ids) => postAppMessage(IFrameCommands.ClearOverriddenElements, ids),
        /** Clears all emphasized elements within the current View */
        clearEmphasizedElements: () => postAppMessage(IFrameCommands.ClearEmphasizedElements),
        zoomElements: (ids) => postAppMessage(IFrameCommands.ZoomElements, ids),
        /** Emphasizes elements with the provided ids.
         *
         * Clears all emphasized elements in the current View before emphasized the provided Elements.
         */
        emphasizeAndZoomElements: (ids) => postAppMessage(IFrameCommands.EmphasizeAndZoomElements, ids),
        queryIModel,
        onSelectionChanged: {
            addListener: (listener) => selectionListeners.add(listener),
            removeListener: (listener) => selectionListeners.delete(listener),
        },
        onSectionRangeChanged: {
            addListener: (listener) => sectionRangeListeners.add(listener),
            removeListener: (listener) => sectionRangeListeners.delete(listener),
        },
        onViewOpened: {
            addListener: (listener) => viewOpenedListeners.add(listener),
            removeListener: (listener) => viewOpenedListeners.delete(listener),
        },
        userStateChanged: {
            addListener: (listener) => viewOpenedListeners.add(listener),
            removeListener: (listener) => viewOpenedListeners.delete(listener),
        },
        customEvent: {
            addListener: addCustomEventListener,
            removeListener: removeCustomEventListener,
        },
        removeEmbeddedApp: () => { },
    };
}
//# sourceMappingURL=EmbedAPI.js.map